import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthClientConfig, AuthConfig, AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { FwSnackbarModule, TranslationService as VisionTranslationService } from '@flywheel-io/vision';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental'
import { withDevtools } from '@tanstack/angular-query-experimental/devtools'

import { AdminService } from './admin/admin.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BasicAuthInterceptor } from './auth.interceptor';
import { AuthService } from './auth.service';
import { ChangeManagerService } from './change-manager.service';
import { ConfigService } from './config.service';
import { DataIOErrorInterceptor } from './data-io-error-interceptor.service';
import { DataViewsService } from './data-views/data-views.service';
import { TasksModule as DeprecatedTasksModule } from './deprecated-tasks/tasks.module';
import { DialogModule } from './dialogs/dialog.module';
import { DownloadService } from './download.service';
import { environment } from '../environments/environment';
import { ExhaustiveModeService } from './exhaustive-mode.service';
import { FatalErrorsModule } from './fatal-errors/fatal-errors.module';
import { CoreApiService } from './core-api.service';
import { GearModule } from './gears/gear.module';
import { GearService } from './gears/gear.service';
import { LoggingService } from './logging.service';
import { ProfileService } from './profile/profile.service';
import { ProjectsService } from './projects/projects.service';
import { QueriesModule } from './queries/queries.module';
import { Auth0Config } from './shared/models/api-config.model';
import { SharedModule } from './shared/shared.module';
import { SidebarService } from './sidebar.service';
import { TaskManagerService } from './task-manager/task-manager.service';
import { TranslationService } from './translation.service';
import { DeidLogService } from './upload/deid-log.service';
import { ViewerModule } from './viewers/viewer.module';
import { ViewerService } from './viewers/viewer.service';
import { XferModule } from './xfer/xfer.module';

function parseAuthConfig(configToParse: Auth0Config, configService: ConfigService): AuthConfig {
  const authAudience = configToParse.audience[0]; // use this to override the audience for auth0 debug
  configService.authAudience = authAudience;

  const centralAPIUri = configService.getCentralConfig().manager_url + '*';

  return {
    ...configToParse,
    clientId: configToParse.client_id,
    authorizationParams: {
      audience: authAudience,
      redirect_uri: `${window.location.origin}`,
    },
    cacheLocation: 'localstorage',
    httpInterceptor: {
      allowedList: [
        {
          uri: '/api/*',
        },
        {
          uri: '/io-proxy/*',
        },
        {
          uri: '/snapshot/*',
        },
        {
          uri: '/xfer/*',
        },
        {
          uri: centralAPIUri,
        },
      ],
    },
  }
}

function initConfig(
  authConfig: AuthClientConfig,
  configService: ConfigService,
) {
  return async () => {
    const apiConfig = await configService.loadApiConfig();
    if (configService.isAuth0Enabled()) {
      authConfig.set(parseAuthConfig(apiConfig.auth.auth0, configService));
    }
    else {
      // dummy config so auth0 doesn't crash the application
      authConfig.set({
        domain: '',
        clientId: '',
        authorizationParams: {
          audience: '',
          redirect_uri: `${window.location.origin}`,
        },
      });
    }
  }
}
@NgModule({ declarations: [
      AppComponent,
    ],
    bootstrap: [AppComponent], imports: [
      // we load the auth config async so we pass nothing here, see initConfig
      AuthModule.forRoot(),
      AppRoutingModule,
      BrowserAnimationsModule,
      BrowserModule,
      // Our modules
      DeprecatedTasksModule,
      DialogModule,
      DeprecatedTasksModule,
      FatalErrorsModule,
      FwSnackbarModule,
      GearModule,
      QueriesModule,
      SharedModule,
      ViewerModule,
      XferModule,
    ], providers: [
      provideAppInitializer(() => {
        const initializerFn = (initConfig)(inject(AuthClientConfig), inject(ConfigService));
        return initializerFn();
      }),
      {
          provide: HTTP_INTERCEPTORS,
          useClass: AuthHttpInterceptor,
          multi: true,
      },
      {
          provide: HTTP_INTERCEPTORS,
          useClass: BasicAuthInterceptor,
          multi: true,
      },
      {
          provide: HTTP_INTERCEPTORS,
          useClass: DataIOErrorInterceptor,
          multi: true,
      },
      {
          provide: ErrorHandler,
          useExisting: LoggingService,
      },
      AdminService,
      AuthService,
      ChangeManagerService,
      ConfigService,
      DataViewsService,
      DeidLogService,
      DownloadService,
      CoreApiService,
      GearService,
      LoggingService,
      ProfileService,
      ProjectsService,
      ExhaustiveModeService,
      SidebarService,
      TaskManagerService,
      TranslationService,
      provideTanStackQuery(new QueryClient({
          defaultOptions: {
              queries: {
                  staleTime: 60000, // 1 minute
              },
          },
      }),
      withDevtools(() => ({ loadDevtools: environment.tanstack_dev_tools, buttonPosition: 'bottom-left' }))),
      {
          // provide translations to Vision components
          provide: VisionTranslationService,
          useExisting: TranslationService,
      },
      ViewerService,
      provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule { }
